/**
 * Client-side role checks. These only control which UI is shown; the backend
 * enforces the same rules via requireRole().
 */

import { Job, JobStatus, User, UserRole } from '../api/types';

/** Job access follows the server's role checks; these helpers only hide UI actions. */
export function canReadJobs(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER || user?.role === UserRole.TECHNICIAN;
}

export function canManageJobs(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

export function canEditJobStatus(user: User | null | undefined, job: Job): boolean {
  return canManageJobs(user) || (user?.role === UserRole.TECHNICIAN && job.assignedTo === user.id);
}

export function canEditJobNotes(user: User | null | undefined, job: Job): boolean {
  return canEditJobStatus(user, job);
}

export function canEditJobLines(user: User | null | undefined, job: Job): boolean {
  return canManageJobs(user) && !job.invoiceId && ![JobStatus.INVOICED, JobStatus.CANCELLED].includes(job.status);
}

/** A flat contract's visit jobs are billed by its period invoices, never on their own (tracker item 34). */
export function canInvoiceJob(user: User | null | undefined, job: Job): boolean {
  return canManageJobs(user) && job.status === JobStatus.COMPLETED && !job.invoiceId && !job.coveredByFlatContract;
}

/**
 * Read customers and properties (Customers nav item and pages, the Dashboard
 * customer widget). Mirrors the backend requireRole('ADMIN', 'OFFICE_MANAGER',
 * 'TECHNICIAN') on GET /customers*, /properties* and /users*: CUSTOMER_PORTAL
 * is denied until the portal adds per-customer scoping.
 */
export function canReadCustomers(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER || user?.role === UserRole.TECHNICIAN;
}

/** Create/update customers: backend requireRole('ADMIN', 'OFFICE_MANAGER'). */
export function canManageCustomers(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/** Create/update/delete properties: backend requireRole('ADMIN', 'OFFICE_MANAGER'). */
export function canManageProperties(user: User | null | undefined): boolean {
  return canManageCustomers(user);
}

/** Create/update/delete tax rates: backend requireRole('ADMIN'). */
export function canManageTaxRates(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/**
 * Edit Settings > Company: backend requireRole('ADMIN') on PUT
 * /company-settings. Every signed-in role may read the settings.
 */
export function canManageCompanySettings(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/** Create/update/delete discount definitions: backend requireRole('ADMIN'). */
export function canManageDiscounts(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/**
 * Create/update/deactivate/delete lead sources: backend
 * requireRole('ADMIN', 'OFFICE_MANAGER'). Reading the catalog (for pickers) is
 * open to any signed-in user; assigning a source to a customer or property
 * follows canManageCustomers / canManageProperties.
 */
export function canManageLeadSources(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * Create/update/delete items (the price list) and item categories: backend
 * requireRole('ADMIN', 'OFFICE_MANAGER'). Reading items is open to any
 * signed-in user.
 */
export function canManageItems(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * Create/update/delete price charts and their bands: backend
 * requireRole('ADMIN', 'OFFICE_MANAGER'). Reading charts is open to any
 * signed-in user (item and job pickers need them).
 */
export function canManagePriceCharts(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * Read checklist templates (the Item form's template picker and name column):
 * backend requireRole('ADMIN', 'OFFICE_MANAGER', 'TECHNICIAN') on GET
 * /checklist-templates. CUSTOMER_PORTAL and OFFICE get 403 (tracker item 44).
 */
export function canReadChecklistTemplates(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER || user?.role === UserRole.TECHNICIAN;
}

/**
 * Settings > Checklist Templates (create, replace, delete / deactivate):
 * backend requireRole('ADMIN', 'OFFICE_MANAGER') (tracker item 44). The page
 * and its Settings card are hidden from every other role.
 */
export function canManageChecklistTemplates(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * A Job's checklist (tracker item 44): GET and tick / untick. Backend:
 * ADMIN / OFFICE_MANAGER on any job, TECHNICIAN only on jobs assigned to
 * them (403 otherwise). Steps can be ticked in every job status.
 */
export function canTickJobChecklist(user: User | null | undefined, job: Pick<Job, 'assignedTo'>): boolean {
  return canManageJobs(user) || (user?.role === UserRole.TECHNICIAN && !!user.id && job.assignedTo === user.id);
}

/** Add / edit / remove steps and apply a template on a Job: ADMIN / OFFICE_MANAGER (any job status). */
export function canEditJobChecklist(user: User | null | undefined): boolean {
  return canManageJobs(user);
}

/**
 * Reports area (nav item, /reports/* routes, customer statement link).
 * Mirrors the backend requireRole('ADMIN', 'OFFICE_MANAGER') on the financial
 * reports. The Jobs report also admits TECHNICIAN on the API, but the UI keeps
 * the whole Reports area office-only.
 */
export function canViewReports(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * Recurring schedules area (/recurring list and detail, the customer page's
 * "Recurring services" section). The backend lets any signed-in user read
 * schedules; the UI keeps them to staff (office roles and TECHNICIAN, who
 * sees them read-only). CUSTOMER_PORTAL has no access.
 */
export function canViewRecurring(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER || user?.role === UserRole.TECHNICIAN;
}

/**
 * Create, pause/resume/cancel, skip dates and generate jobs: backend
 * requireRole('ADMIN', 'OFFICE_MANAGER') on every recurring-jobs write.
 */
export function canManageRecurring(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * Journal ledger (/ledger list, entry detail, manual entry, reverse, and the
 * Chart of Accounts "View activity" link). Mirrors the backend
 * requireRole('ADMIN', 'OFFICE_MANAGER') on every /gl/journal-entries route.
 */
export function canViewLedger(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * Settings > Audit log (/settings/audit-log route and its settings card).
 * Mirrors the backend requireRole('ADMIN') on every /audit-logs route.
 */
export function canViewAuditLog(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/**
 * Inventory page (/inventory nav item and route: valuation, Receive stock,
 * Adjust count, per-item history). Mirrors the backend
 * requireRole('ADMIN', 'OFFICE_MANAGER') on receive, adjust and valuation.
 * The history endpoint admits any signed-in user, but the page is office-only.
 */
export function canManageInventory(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/** Create/update GL accounts and seed the chart: backend requireRole('ADMIN'). */
export function canManageGLAccounts(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/**
 * Access to the Settings > Users page: backend requireRole('ADMIN', 'OFFICE_MANAGER')
 * on the user-management mutations. Per-target limits are canManageUserRole.
 */
export function canManageUsers(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/** Roles an OFFICE_MANAGER may manage or assign (backend OFFICE_MANAGER_MANAGEABLE_ROLES). */
const OFFICE_MANAGER_MANAGEABLE_ROLES: readonly string[] = [UserRole.TECHNICIAN, UserRole.CUSTOMER_PORTAL];

/**
 * Whether `actorRole` may manage a user whose role is `targetRole`, or assign
 * `targetRole` on create/update. Mirrors backend UsersBusinessLayer.canManageRole:
 *  - ADMIN: any role
 *  - OFFICE_MANAGER: only TECHNICIAN / CUSTOMER_PORTAL
 *  - everyone else (incl. deprecated OFFICE): nothing
 */
export function canManageUserRole(
  actorRole: UserRole | string | null | undefined,
  targetRole: UserRole | string
): boolean {
  if (actorRole === UserRole.ADMIN) return true;
  if (actorRole === UserRole.OFFICE_MANAGER) return OFFICE_MANAGER_MANAGEABLE_ROLES.includes(targetRole);
  return false;
}

/** Roles the backend accepts on create/update (deprecated OFFICE is rejected). */
export const ASSIGNABLE_USER_ROLES: readonly UserRole[] = [
  UserRole.ADMIN,
  UserRole.OFFICE_MANAGER,
  UserRole.TECHNICIAN,
  UserRole.CUSTOMER_PORTAL,
];

/** The subset of ASSIGNABLE_USER_ROLES that `actorRole` may assign. */
export function assignableRolesFor(actorRole: UserRole | string | null | undefined): UserRole[] {
  return ASSIGNABLE_USER_ROLES.filter((role) => canManageUserRole(actorRole, role));
}

/**
 * Settings > Notifications (provider connection, templates, master switch,
 * test send): backend requireRole('ADMIN') (tracker item 35).
 */
export function canManageNotifications(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/**
 * Email invoices and statements, read message history, retry / cancel
 * messages: backend requireRole('ADMIN', 'OFFICE_MANAGER').
 */
export function canSendNotifications(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * Dispatch board (/dispatch nav item and route, the day-sheet print, Settings >
 * Technician schedules). Mirrors the backend requireRole('ADMIN',
 * 'OFFICE_MANAGER') on every /dispatch route (tracker item 40): technicians
 * keep the job list (their own view is item 42), the customer portal has none.
 */
export function canViewDispatch(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * Work Orders (/work-orders nav item and page, tracker item 58): bulk work
 * order printing and confirmation. Mirrors the backend requireRole('ADMIN',
 * 'OFFICE_MANAGER') on every /work-order-batches route; technicians keep the
 * individual /print/jobs/:id service order.
 */
export function canPrintWorkOrders(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/** Schedule, reassign and unschedule jobs; edit working hours and time off (same roles as viewing). */
export function canManageDispatch(user: User | null | undefined): boolean {
  return canViewDispatch(user);
}

/**
 * Settings > Payments: connect / disconnect the card processor, the master
 * switch and autopay settings (backend requireRole('ADMIN'); tracker item 38).
 */
export function canManageOnlinePayments(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/**
 * Cards on file, charging a card on file, the online payment attempts page
 * and reconcile (backend requireRole('ADMIN', 'OFFICE_MANAGER')).
 */
export function canUseOnlinePayments(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/** Refund an online card payment (backend requireRole('ADMIN')). */
export function canRefundOnlinePayments(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/**
 * Attachments on customers, properties and jobs (tracker item 47): list,
 * view, download and upload. Mirrors the backend requireRole('ADMIN',
 * 'OFFICE_MANAGER', 'TECHNICIAN') on GET / POST
 * /{customers|properties|jobs}/:id/attachments and GET /attachments/:id[/download].
 * TECHNICIAN reaches every parent in the tenant (not only assigned jobs);
 * CUSTOMER_PORTAL and the deprecated OFFICE role get nothing.
 */
export function canUploadAttachments(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER || user?.role === UserRole.TECHNICIAN;
}

/** Viewing attachments uses the same roles as uploading them. */
export function canViewAttachments(user: User | null | undefined): boolean {
  return canUploadAttachments(user);
}

/** Rename, edit the description and delete: backend requireRole('ADMIN', 'OFFICE_MANAGER') on PATCH / DELETE /attachments/:id. */
export function canManageAttachments(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * Open the technician view (/tech): backend staff roles of the technician
 * module (ADMIN, OFFICE_MANAGER, TECHNICIAN; tracker item 42). CUSTOMER_PORTAL
 * and the deprecated OFFICE role get 403 there.
 */
export function canUseTechnicianView(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER || user?.role === UserRole.TECHNICIAN;
}

/** List another user's day (GET /technician/today?userId=): office roles only. */
export function canViewOtherTechnicianDay(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * The customer portal (/portal/*, tracker item 45): backend CUSTOMER_PORTAL
 * only; every staff role is 403 on /api/portal/* (except the public
 * registration and password routes). Portal users in turn never see a staff
 * route or nav item.
 */
export function canUsePortal(user: User | null | undefined): boolean {
  return user?.role === UserRole.CUSTOMER_PORTAL;
}

/**
 * The requests inbox (service, reschedule and portal access requests), a
 * customer's portal logins (invite, resend, disable, unlink) and an invoice's
 * portal code (show, regenerate): backend requireRole('ADMIN', 'OFFICE_MANAGER')
 * on /service-requests, /reschedule-requests, /portal-access-requests,
 * /customers/:id/portal* and /invoices/:id/portal-code/regenerate.
 * TECHNICIAN and CUSTOMER_PORTAL are 403 (tracker item 45).
 */
/** Estimates / quotes (tracker item 39): backend /api/estimates is ADMIN / OFFICE_MANAGER only (prices). */
export function canManageEstimates(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

export function canManageRequests(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/** Invite / resend / disable / unlink a customer's portal logins, and regenerate an invoice's portal code. Same roles as the inbox. */
export function canManageCustomerPortal(user: User | null | undefined): boolean {
  return canManageRequests(user);
}

/** Settings > Customer portal: the portal fields go through PUT /company-settings, backend requireRole('ADMIN'). */
export function canManagePortalSettings(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/** Settings > Web form intake (tracker item 49): backend requireRole('ADMIN') on /service-request-intake. */
export function canManageWebFormIntake(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/**
 * Property location (tracker item 41): see coordinates, set a pin by hand,
 * look an address up again. Backend requireRole('ADMIN', 'OFFICE_MANAGER');
 * other roles get properties without coordinates.
 */
export function canManagePropertyLocation(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/** Dispatch "Suggest" slots (tracker item 41, phase 2): backend POST /dispatch/suggestions is ADMIN / OFFICE_MANAGER. */
export function canUseRouteSuggestions(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/** Settings > Geocoding (tracker item 41): backend requireRole('ADMIN') on /geocoding. */
export function canManageGeocoding(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/** Settings > Import / Export, import part (tracker item 52): backend requireRole('ADMIN') on /import-export imports and templates. */
export function canImportData(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/** Settings > Import / Export, export part (tracker item 52): backend requireRole('ADMIN', 'OFFICE_MANAGER') on /import-export/exports. */
export function canExportData(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/** Own two-step verification page (tracker item 63): every staff role (backend MFA_STAFF_ROLES). */
export function canUseMfa(user: User | null | undefined): boolean {
  return !!user && user.role !== UserRole.CUSTOMER_PORTAL;
}

/** Settings > Two-step verification policy and the Users page MFA reset (tracker item 63): backend requireRole('ADMIN'). */
export function canManageMfaPolicy(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/** Everything outside /portal (the staff app): every signed-in role but CUSTOMER_PORTAL. */
export function canUseStaffApp(user: User | null | undefined): boolean {
  return !!user && user.role !== UserRole.CUSTOMER_PORTAL;
}

/** After login: a CUSTOMER_PORTAL user lands on /portal, a TECHNICIAN on /tech, everyone else on the dashboard. */
export function homePathFor(user: User | null | undefined): string {
  if (user?.role === UserRole.CUSTOMER_PORTAL) return '/portal';
  return user?.role === UserRole.TECHNICIAN ? '/tech' : '/';
}

/**
 * Global search (tracker item 61): the Ctrl/Cmd-K palette and its buttons.
 * Mirrors the backend requireRole('ADMIN', 'OFFICE_MANAGER', 'TECHNICIAN') on
 * GET /search; the backend also narrows the result types per role
 * (technicians get customers, properties and jobs only).
 */
export function canUseGlobalSearch(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER || user?.role === UserRole.TECHNICIAN;
}

/** See and change the guided setup checklist: backend requireRole('ADMIN') on /setup (tracker item 75). */
export function canManageSetup(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/**
 * Industry starter packs (Settings > Industry starter packs, the "Starter"
 * badge and Keep on the catalog lists, the setup wizard entry; tracker item
 * 76): backend requireRole('ADMIN') on every /starter-templates route.
 */
export function canManageStarterTemplates(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/**
 * Recurring presets list and the "Start from a preset" select: backend reads
 * (GET /recurring-presets) are open to every non-portal user; the UI keeps
 * them to staff like canViewRecurring (tracker item 76).
 */
export function canViewRecurringPresets(user: User | null | undefined): boolean {
  return canViewRecurring(user);
}

/** Create, edit and delete recurring presets: backend requireRole('ADMIN', 'OFFICE_MANAGER') (tracker item 76). */
export function canManageRecurringPresets(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * The staff notification bell, the /notifications page and Settings > My
 * alerts (tracker item 62): backend requireRole('ADMIN', 'OFFICE_MANAGER') on
 * every /staff-notifications route. Technicians have no routed alert in v1.
 */
export function canViewStaffNotifications(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * Customer satisfaction surveys (tracker item 69): the inbox, a job's survey
 * section, the satisfaction report and the settings page (read). Backend
 * requireRole('ADMIN', 'OFFICE_MANAGER') on /surveys, /jobs/:id/survey and
 * /reports/customer-satisfaction. TECHNICIAN gets no access in v1.
 */
export function canViewSurveys(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/** Change the survey settings: backend requireRole('ADMIN') on PUT /surveys/settings. */
export function canManageSurveys(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}

/**
 * Portal rollout campaigns (tracker item 81): Settings > Portal rollout, the campaign pages and the
 * customer page's rollout history. Backend requireRole('ADMIN', 'OFFICE_MANAGER') on every
 * /portal-rollout route (OFFICE, TECHNICIAN and CUSTOMER_PORTAL are 403).
 */
export function canManagePortalRollout(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/**
 * Service bundles (tracker item 85): Settings > Service bundles, the Bundles
 * cards, the enrollment page and the Renewals list. Backend
 * requireRole('ADMIN', 'OFFICE_MANAGER') on bundle writes and every
 * /enrollments route; technicians see the visit chip on their jobs only.
 */
export function canManageBundles(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;
}

/** Settings > Service bundles > Alerts: backend requireRole('ADMIN') on /settings/bundle-alerts. */
export function canManageBundleAlerts(user: User | null | undefined): boolean {
  return user?.role === UserRole.ADMIN;
}
